<template>
  <!-- Default design -->
  <div
    v-if="design === 'default'"
    class="date-tile"
    :class="[`date-tile--${size}`]"
  >
    <div
      class="date-tile__header"
      :class="[color]"
    >
      {{ monthText }}
    </div>
    <div class="date-tile__day border-primary">
      {{ dayText }}
    </div>
    <div class="date-tile__footer border-primary">
      <div class="date-tile__weekday border-primary">
        {{ weekdayText }}
      </div>
      <div class="date-tile__time">
        {{ timeText }}
      </div>
    </div>
  </div>

  <!-- Minimal design -->
  <div
    v-if="design === 'minimal'"
    class="w-[90px] px-3 py-2 flex flex-col items-center justify-between rounded-lg bg-secondary border border-secondary"
  >
    <div class="font-semibold text-quaternary">
      {{ weekdayText }}
    </div>
    <div class="font-bold text-3xl">{{ dayText }}</div>
    <div class="text-md font-semibold text-quaternary">
      {{ monthYearText }}
    </div>
    <div
      v-if="showTime"
      class="flex items-center justify-center gap-1 text-secondary mt-2 pt-2 border-t border-secondary w-full"
    >
      <Icon
        type="clock"
        class="text-sm"
      />
      <span class="text-sm">{{ timeText }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
// components
import Icon from '@/components/icon/Icon.vue'
// libraries
import { computed } from 'vue'
import dayjs from 'dayjs'

/**
 * Props accepted by DateTile component
 */
interface DateTileProps {
  /**
   * ISO date string used to render the tile (e.g. 2021-01-01T10:30:00)
   */
  date: string | null
  /**
   * Tailwind background color utility class applied to the header (e.g. bg-primary-solid)
   */
  color: string | null
  /**
   * Visual size variant for the tile
   */
  size: 'sm' | 'md' | 'lg'
  /**
   * Design variant for the tile
   */
  design: 'default' | 'minimal'
  /**
   * Whether to show the time with clock icon (only applies to minimal design)
   */
  showTime: boolean
}

const props = withDefaults(defineProps<DateTileProps>(), {
  date: null,
  color: null,
  size: 'md',
  design: 'default',
  showTime: false
})

/**
 * Month text derived from `date` prop, formatted as MMM. Returns empty string when invalid.
 */
const monthText = computed<string>(() => {
  try {
    const d = dayjs(props.date)
    return d.isValid() ? d.format('MMM') : ''
  } catch {
    return ''
  }
})

/**
 * Day of month derived from `date` prop. Returns empty string when invalid.
 */
const dayText = computed<string>(() => {
  try {
    const d = dayjs(props.date)
    return d.isValid() ? d.format('D') : ''
  } catch {
    return ''
  }
})

/**
 * Weekday derived from `date` prop. Returns empty string when invalid.
 */
const weekdayText = computed<string>(() => {
  try {
    const d = dayjs(props.date)
    return d.isValid() ? d.format('ddd') : ''
  } catch {
    return ''
  }
})

/**
 * Time derived from `date` prop. Returns empty string when invalid.
 */
const timeText = computed<string>(() => {
  try {
    const d = dayjs(props.date)
    return d.isValid() ? d.format('H:mm') : ''
  } catch {
    return ''
  }
})

/**
 * Month and year derived from `date` prop, formatted as MMM YY. Returns empty string when invalid.
 */
const monthYearText = computed<string>(() => {
  try {
    const d = dayjs(props.date)
    return d.isValid() ? d.format('MMM YY') : ''
  } catch {
    return ''
  }
})
</script>

<style scoped>
@reference "../../assets/css/tailwind.css";

.date-tile {
  @apply flex flex-col;
}

/* size variants - container */
.date-tile--sm {
  @apply w-20 h-24;
}
.date-tile--md {
  @apply w-24 h-28;
}
.date-tile--lg {
  @apply w-28 h-32;
}

/* header */
.date-tile__header {
  @apply p-1 flex justify-center rounded-t-xl text-white font-medium;
}
.date-tile--sm .date-tile__header {
  @apply text-sm;
}
.date-tile--md .date-tile__header {
  @apply text-base;
}
.date-tile--lg .date-tile__header {
  @apply text-lg;
}

/* day cell */
.date-tile__day {
  @apply font-semibold border-x flex flex-1 justify-center items-center;
}
.date-tile--sm .date-tile__day {
  @apply text-xl;
}
.date-tile--md .date-tile__day {
  @apply text-2xl;
}
.date-tile--lg .date-tile__day {
  @apply text-3xl;
}

/* footer */
.date-tile__footer {
  @apply flex border rounded-b-xl;
}
.date-tile__weekday {
  @apply text-center p-1 border-r w-full;
}
.date-tile__time {
  @apply w-full p-1 text-center;
}
</style>
